/**
 * MinistrareIA - Onboarding Inputs
 * Todos os tipos de input para o onboarding
 * 
 * UPDATED: 
 * - FAQ cards mais largos (380px)
 * - Scroll vai até a borda esquerda da tela
 * - Padding bottom correto no card
 */
import React, { useState, useRef, createElement } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { 
  Mic, MicOff, Send, Keyboard, ArrowRight, Check,
  Plus, Trash2, Loader2, HelpCircle
} from 'lucide-react';

// ============================================
// TEXT ONLY INPUT (sem áudio, com limite)
// ============================================

export const TextOnlyInput = ({
  onSubmit,
  onSkip,
  placeholder = 'Digite aqui...',
  maxLength = 200,
  minLength = 10,
  skipText = 'Pular',
  required = true
}) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const [value, setValue] = useState('');

  const canSubmit = value.trim().length >= minLength;
  const remaining = maxLength - value.length;

  const handleSubmit = () => {
    if (canSubmit) {
      onSubmit(value.trim());
      setValue('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && canSubmit) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChange = (e) => {
    if (e.target.value.length <= maxLength) {
      setValue(e.target.value);
    }
  };

  const styles = {
    container: {
      backgroundColor: isDark ? colors.bgPrimary : '#ffffff',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '16px',
      padding: '20px',
    },
    textArea: {
      width: '100%',
      padding: '14px 16px',
      backgroundColor: isDark ? colors.bgSecondary : '#f9fafb',
      border: `2px solid ${colors.borderColor}`,
      borderRadius: '12px',
      fontSize: '14px',
      color: colors.textPrimary,
      resize: 'none',
      outline: 'none',
      fontFamily: 'inherit',
      transition: 'border-color 0.2s ease',
      minHeight: '80px',
    },
    footer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: '12px',
    },
    charCount: {
      fontSize: '12px',
      color: remaining < 20 ? '#ef4444' : colors.textTertiary,
    },
    submitButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '12px 24px',
      backgroundColor: canSubmit ? colors.accentPrimary : colors.borderColor,
      color: canSubmit ? 'white' : colors.textTertiary,
      border: 'none',
      borderRadius: '10px',
      fontSize: '14px',
      fontWeight: 600,
      cursor: canSubmit ? 'pointer' : 'not-allowed',
      transition: 'all 0.2s ease',
    },
    skipButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      width: '100%',
      padding: '12px',
      backgroundColor: 'transparent',
      color: colors.textTertiary,
      border: 'none',
      fontSize: '13px',
      cursor: 'pointer',
      marginTop: '12px',
    },
  };

  return (
    <div style={styles.container}>
      <textarea
        style={styles.textArea}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        onFocus={(e) => {
          e.target.style.borderColor = colors.accentPrimary;
        }}
        onBlur={(e) => {
          e.target.style.borderColor = colors.borderColor;
        }}
      />
      
      <div style={styles.footer}>
        <span style={styles.charCount}>
          {remaining} caracteres restantes
        </span>
        <button
          style={styles.submitButton}
          onClick={handleSubmit}
          disabled={!canSubmit}
        >
          <Send size={16} />
          Enviar
        </button>
      </div>

      {!required && (
        <button style={styles.skipButton} onClick={onSkip}>
          {skipText}
          <ArrowRight size={14} />
        </button>
      )}
    </div>
  );
};

// ============================================
// TEXT/AUDIO INPUT
// ============================================

export const TextAudioInput = ({
  onSubmit,
  onSkip,
  placeholder = 'Digite aqui...',
  minLength = 10,
  skipText = 'Pular',
  required = true,
  isProcessingAudio = false,
  onTranscribe
}) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const [value, setValue] = useState('');
  const [showTextInput, setShowTextInput] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  const canSubmit = value.trim().length >= minLength;

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        if (onTranscribe) {
          const transcript = await onTranscribe(audioBlob);
          if (transcript) {
            onSubmit(transcript);
          }
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Erro ao acessar microfone:', error);
      setShowTextInput(true);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleAudioToggle = () => {
    if (!isRecording) {
      startRecording();
    } else {
      stopRecording();
    }
  };

  const handleSubmit = () => {
    if (canSubmit) {
      onSubmit(value);
      setValue('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && canSubmit) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const styles = {
    container: {
      backgroundColor: isDark ? colors.bgPrimary : '#ffffff',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '16px',
      padding: '24px',
    },
    micButton: {
      width: '80px',
      height: '80px',
      borderRadius: '50%',
      border: 'none',
      backgroundColor: isRecording ? '#EF4444' : colors.accentPrimary,
      color: 'white',
      cursor: isProcessingAudio ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 16px',
      position: 'relative',
      transition: 'all 0.2s ease',
      opacity: isProcessingAudio ? 0.6 : 1,
    },
    micPulse: {
      position: 'absolute',
      inset: '-8px',
      borderRadius: '50%',
      border: '3px solid #EF4444',
      animation: 'micPulse 1.5s ease-out infinite',
      opacity: 0.5,
    },
    micLabel: {
      textAlign: 'center',
      fontSize: '14px',
      color: colors.textSecondary,
      marginBottom: '20px',
    },
    divider: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      margin: '20px 0',
    },
    dividerLine: {
      flex: 1,
      height: '1px',
      backgroundColor: colors.borderColor,
    },
    dividerText: {
      fontSize: '12px',
      color: colors.textTertiary,
      fontWeight: 500,
    },
    textToggle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '12px 16px',
      backgroundColor: isDark ? colors.bgSecondary : '#f9fafb',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '10px',
      cursor: 'pointer',
      width: '100%',
      fontSize: '14px',
      color: colors.textSecondary,
      fontWeight: 500,
      transition: 'all 0.2s ease',
    },
    textArea: {
      width: '100%',
      padding: '14px 16px',
      backgroundColor: isDark ? colors.bgSecondary : '#f9fafb',
      border: `2px solid ${colors.borderColor}`,
      borderRadius: '12px',
      fontSize: '14px',
      color: colors.textPrimary,
      resize: 'none',
      outline: 'none',
      fontFamily: 'inherit',
      transition: 'border-color 0.2s ease',
      minHeight: '100px',
    },
    charCount: {
      textAlign: 'right',
      fontSize: '12px',
      color: canSubmit ? colors.accentPrimary : colors.textTertiary,
      marginTop: '8px',
    },
    submitButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      width: '100%',
      padding: '14px 20px',
      backgroundColor: canSubmit ? colors.accentPrimary : colors.borderColor,
      color: canSubmit ? 'white' : colors.textTertiary,
      border: 'none',
      borderRadius: '10px',
      fontSize: '14px',
      fontWeight: 600,
      cursor: canSubmit ? 'pointer' : 'not-allowed',
      marginTop: '12px',
      transition: 'all 0.2s ease',
    },
    skipButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      width: '100%',
      padding: '12px',
      backgroundColor: 'transparent',
      color: colors.textTertiary,
      border: 'none',
      fontSize: '13px',
      cursor: 'pointer',
      marginTop: '12px',
    },
  };

  return (
    <div style={styles.container}>
      <button
        style={styles.micButton}
        onClick={handleAudioToggle}
        disabled={isProcessingAudio}
      >
        {isProcessingAudio ? (
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
        ) : isRecording ? (
          <>
            <MicOff size={32} />
            <span style={styles.micPulse} />
          </>
        ) : (
          <Mic size={32} />
        )}
      </button>

      <p style={styles.micLabel}>
        {isProcessingAudio 
          ? 'Transcrevendo...' 
          : isRecording 
            ? 'Gravando... Clique para parar' 
            : 'Clique para gravar'}
      </p>

      <div style={styles.divider}>
        <div style={styles.dividerLine} />
        <span style={styles.dividerText}>ou</span>
        <div style={styles.dividerLine} />
      </div>

      {!showTextInput ? (
        <button
          style={styles.textToggle}
          onClick={() => setShowTextInput(true)}
        >
          <Keyboard size={18} />
          Prefiro digitar
        </button>
      ) : (
        <>
          <textarea
            style={styles.textArea}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            onFocus={(e) => {
              e.target.style.borderColor = colors.accentPrimary;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = colors.borderColor;
            }}
          />
          <p style={styles.charCount}>
            {value.length < minLength 
              ? `Mínimo ${minLength - value.length} caracteres` 
              : `✓ ${value.length} caracteres`}
          </p>
          <button
            style={styles.submitButton}
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            <Send size={18} />
            Enviar
          </button>
        </>
      )}

      {!required && (
        <button style={styles.skipButton} onClick={onSkip}>
          {skipText}
          <ArrowRight size={14} />
        </button>
      )}

      <style>{`
        @keyframes micPulse {
          0% { transform: scale(1); opacity: 0.5; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ============================================
// OPTIONS INPUT (Single Select)
// ============================================

export const OptionsInput = ({ options, onSelect, onSkip, skipText, required = true }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const [selectedId, setSelectedId] = useState(null);

  const handleSelect = (option) => {
    setSelectedId(option.id);
    setTimeout(() => onSelect(option), 200);
  };

  const styles = {
    container: {
      display: 'grid',
      gridTemplateColumns: options.length <= 2 ? '1fr' : 'repeat(2, 1fr)',
      gap: '12px',
    },
    option: (isSelected) => ({
      padding: '16px',
      backgroundColor: isSelected 
        ? colors.accentPrimary 
        : (isDark ? colors.bgPrimary : '#ffffff'),
      border: `2px solid ${isSelected ? colors.accentPrimary : colors.borderColor}`,
      borderRadius: '12px',
      cursor: selectedId ? 'default' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      transition: 'all 0.2s ease',
      opacity: selectedId && !isSelected ? 0.5 : 1,
    }),
    optionIcon: (isSelected) => ({
      width: '44px',
      height: '44px',
      borderRadius: '10px',
      backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : `${colors.accentPrimary}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: isSelected ? 'white' : colors.accentPrimary,
      flexShrink: 0,
    }),
    optionContent: {
      flex: 1,
    },
    optionLabel: (isSelected) => ({
      fontSize: '14px',
      fontWeight: 600,
      color: isSelected ? 'white' : colors.textPrimary,
      marginBottom: '2px',
    }),
    optionDesc: (isSelected) => ({
      fontSize: '12px',
      color: isSelected ? 'rgba(255,255,255,0.8)' : colors.textSecondary,
    }),
    checkIcon: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    skipButton: {
      gridColumn: '1 / -1',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      padding: '12px',
      backgroundColor: 'transparent',
      color: colors.textTertiary,
      border: 'none',
      fontSize: '13px',
      cursor: 'pointer',
      marginTop: '8px',
    },
  };

  return (
    <div style={styles.container}>
      {options.map((option) => {
        const isSelected = selectedId === option.id;
        const IconComponent = option.icon;

        return (
          <div
            key={option.id}
            style={styles.option(isSelected)}
            onClick={() => !selectedId && handleSelect(option)}
          >
            <div style={styles.optionIcon(isSelected)}>
              {IconComponent && createElement(IconComponent, { size: 20 })}
            </div>
            <div style={styles.optionContent}>
              <div style={styles.optionLabel(isSelected)}>{option.label}</div>
              {option.description && (
                <div style={styles.optionDesc(isSelected)}>{option.description}</div>
              )}
            </div>
            {isSelected && (
              <div style={styles.checkIcon}>
                <Check size={14} color={colors.accentPrimary} strokeWidth={3} />
              </div>
            )}
          </div>
        );
      })}

      {!required && skipText && (
        <button style={styles.skipButton} onClick={onSkip}>
          {skipText}
          <ArrowRight size={14} />
        </button>
      )}
    </div>
  );
};

// ============================================
// OPTIONS WITH CUSTOM INPUT
// ============================================

export const OptionsCustomInput = ({ 
  options, 
  onSelect, 
  customPlaceholder = 'Digite uma opção personalizada...' 
}) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const [selectedId, setSelectedId] = useState(null);
  const [showCustom, setShowCustom] = useState(false);
  const [customValue, setCustomValue] = useState('');

  const handleSelect = (option) => {
    setSelectedId(option.id);
    setShowCustom(false);
    setTimeout(() => onSelect({ ...option, custom: null }), 200);
  };

  const handleCustomSubmit = () => {
    if (customValue.trim().length >= 5) {
      onSelect({ id: 'custom', label: 'Personalizado', custom: customValue });
    }
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    optionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px',
    },
    option: (isSelected) => ({
      padding: '14px',
      backgroundColor: isSelected 
        ? colors.accentPrimary 
        : (isDark ? colors.bgPrimary : '#ffffff'),
      border: `2px solid ${isSelected ? colors.accentPrimary : colors.borderColor}`,
      borderRadius: '12px',
      cursor: selectedId && !showCustom ? 'default' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      transition: 'all 0.2s ease',
      opacity: selectedId && !isSelected && !showCustom ? 0.5 : 1,
    }),
    optionIcon: (isSelected) => ({
      width: '36px',
      height: '36px',
      borderRadius: '8px',
      backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : `${colors.accentPrimary}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: isSelected ? 'white' : colors.accentPrimary,
      flexShrink: 0,
    }),
    optionLabel: (isSelected) => ({
      flex: 1,
      fontSize: '13px',
      fontWeight: 600,
      color: isSelected ? 'white' : colors.textPrimary,
    }),
    customToggle: {
      padding: '14px',
      backgroundColor: isDark ? colors.bgPrimary : '#ffffff',
      border: `2px dashed ${colors.borderColor}`,
      borderRadius: '12px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      color: colors.textSecondary,
      fontSize: '13px',
      fontWeight: 500,
      gridColumn: '1 / -1',
    },
    customInput: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      padding: '16px',
      backgroundColor: isDark ? colors.bgPrimary : '#ffffff',
      border: `2px solid ${colors.accentPrimary}`,
      borderRadius: '12px',
    },
    textArea: {
      width: '100%',
      padding: '12px',
      backgroundColor: isDark ? colors.bgSecondary : '#f9fafb',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '8px',
      fontSize: '14px',
      color: colors.textPrimary,
      resize: 'none',
      outline: 'none',
      fontFamily: 'inherit',
    },
    customActions: {
      display: 'flex',
      gap: '8px',
    },
    cancelBtn: {
      flex: 1,
      padding: '10px',
      backgroundColor: 'transparent',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '8px',
      color: colors.textSecondary,
      fontSize: '13px',
      cursor: 'pointer',
    },
    submitBtn: {
      flex: 1,
      padding: '10px',
      backgroundColor: customValue.length >= 5 ? colors.accentPrimary : colors.borderColor,
      border: 'none',
      borderRadius: '8px',
      color: customValue.length >= 5 ? 'white' : colors.textTertiary,
      fontSize: '13px',
      fontWeight: 600,
      cursor: customValue.length >= 5 ? 'pointer' : 'not-allowed',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.optionsGrid}>
        {options.map((option) => {
          const isSelected = selectedId === option.id && !showCustom;
          const IconComponent = option.icon;

          return (
            <div
              key={option.id}
              style={styles.option(isSelected)}
              onClick={() => !selectedId || showCustom ? handleSelect(option) : null}
            >
              <div style={styles.optionIcon(isSelected)}>
                {IconComponent && createElement(IconComponent, { size: 18 })}
              </div>
              <span style={styles.optionLabel(isSelected)}>{option.label}</span>
              {isSelected && <Check size={16} color="white" />}
            </div>
          );
        })}

        {!showCustom && !selectedId && (
          <div
            style={styles.customToggle}
            onClick={() => setShowCustom(true)}
          >
            <Plus size={16} />
            Personalizado
          </div>
        )}
      </div>

      {showCustom && (
        <div style={styles.customInput}>
          <textarea
            style={styles.textArea}
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            placeholder={customPlaceholder}
            rows={2}
            autoFocus
          />
          <div style={styles.customActions}>
            <button
              style={styles.cancelBtn}
              onClick={() => {
                setShowCustom(false);
                setCustomValue('');
              }}
            >
              Cancelar
            </button>
            <button
              style={styles.submitBtn}
              onClick={handleCustomSubmit}
              disabled={customValue.length < 5}
            >
              Usar este tom
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// MULTISELECT WITH TEXT
// ============================================

export const MultiselectTextInput = ({ 
  options, 
  onSubmit, 
  textPlaceholder = 'Adicione regras personalizadas...' 
}) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const [selected, setSelected] = useState([]);
  const [additionalText, setAdditionalText] = useState('');

  const toggleOption = (optionId) => {
    setSelected(prev => 
      prev.includes(optionId) 
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    );
  };

  const handleSubmit = () => {
    if (selected.length > 0 || additionalText.trim().length > 0) {
      onSubmit({ selected, additional: additionalText.trim() });
    }
  };

  const canSubmit = selected.length > 0 || additionalText.trim().length >= 5;

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    optionsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '10px',
    },
    option: (isSelected) => ({
      padding: '12px',
      backgroundColor: isSelected 
        ? `${colors.accentPrimary}15` 
        : (isDark ? colors.bgPrimary : '#ffffff'),
      border: `2px solid ${isSelected ? colors.accentPrimary : colors.borderColor}`,
      borderRadius: '10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      transition: 'all 0.2s ease',
    }),
    checkbox: (isSelected) => ({
      width: '20px',
      height: '20px',
      borderRadius: '6px',
      border: `2px solid ${isSelected ? colors.accentPrimary : colors.borderColor}`,
      backgroundColor: isSelected ? colors.accentPrimary : 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      transition: 'all 0.2s ease',
    }),
    optionLabel: {
      fontSize: '13px',
      fontWeight: 500,
      color: colors.textPrimary,
    },
    textArea: {
      width: '100%',
      padding: '14px',
      backgroundColor: isDark ? colors.bgPrimary : '#ffffff',
      border: `2px solid ${colors.borderColor}`,
      borderRadius: '10px',
      fontSize: '14px',
      color: colors.textPrimary,
      resize: 'none',
      outline: 'none',
      fontFamily: 'inherit',
      transition: 'border-color 0.2s ease',
    },
    submitButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '14px 20px',
      backgroundColor: canSubmit ? colors.accentPrimary : colors.borderColor,
      color: canSubmit ? 'white' : colors.textTertiary,
      border: 'none',
      borderRadius: '10px',
      fontSize: '14px',
      fontWeight: 600,
      cursor: canSubmit ? 'pointer' : 'not-allowed',
      marginTop: '4px',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.optionsGrid}>
        {options.map((option) => {
          const isSelected = selected.includes(option.id);
          
          return (
            <div
              key={option.id}
              style={styles.option(isSelected)}
              onClick={() => toggleOption(option.id)}
            >
              <div style={styles.checkbox(isSelected)}>
                {isSelected && <Check size={12} color="white" strokeWidth={3} />}
              </div>
              <span style={styles.optionLabel}>{option.label}</span>
            </div>
          );
        })}
      </div>

      <textarea
        style={styles.textArea}
        value={additionalText}
        onChange={(e) => setAdditionalText(e.target.value)}
        placeholder={textPlaceholder}
        rows={2}
        onFocus={(e) => {
          e.target.style.borderColor = colors.accentPrimary;
        }}
        onBlur={(e) => {
          e.target.style.borderColor = colors.borderColor;
        }}
      />

      <button
        style={styles.submitButton}
        onClick={handleSubmit}
        disabled={!canSubmit}
      >
        Continuar
        <ArrowRight size={18} />
      </button>
    </div>
  );
};

// ============================================
// FAQ CARDS INPUT (Horizontal Scroll)
// ============================================

export const FAQCardsInput = ({ onSubmit, onSkip, skipText, maxItems = 15 }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const scrollRef = useRef(null);
  const [items, setItems] = useState([{ id: 1, question: '', answer: '' }]);

  // Tamanho padrão dos cards - mais largo
  const CARD_WIDTH = 400;
  const CARD_MIN_HEIGHT = 260;

  const addItem = () => {
    if (items.length < maxItems) {
      const newId = Date.now();
      setItems([...items, { id: newId, question: '', answer: '' }]);
      
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTo({
            left: scrollRef.current.scrollWidth,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  };

  const removeItem = (id) => {
    if (items.length > 1) {
      setItems(items.filter(item => item.id !== id));
    }
  };

  const updateItem = (id, field, value) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const handleSubmit = () => {
    const validItems = items.filter(
      item => item.question.trim().length >= 5 && item.answer.trim().length >= 5
    );
    if (validItems.length > 0) {
      onSubmit({ items: validItems });
    }
  };

  const validCount = items.filter(
    item => item.question.trim().length >= 5 && item.answer.trim().length >= 5
  ).length;

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
    },
    scrollContainer: {
      overflowX: 'auto',
      overflowY: 'hidden',
      // Margem negativa grande para ir até a borda da tela
      marginLeft: 'calc(-50vw + 50%)',
      marginRight: 'calc(-50vw + 50%)',
      // Padding para dar espaço nas bordas
      paddingLeft: 'calc(50vw - 50% + 24px)',
      paddingRight: '24px',
      paddingTop: '4px',
      paddingBottom: '12px',
    },
    cardsWrapper: {
      display: 'flex',
      gap: '16px',
    },
    card: {
      width: `${CARD_WIDTH}px`,
      minWidth: `${CARD_WIDTH}px`,
      minHeight: `${CARD_MIN_HEIGHT}px`,
      backgroundColor: isDark ? colors.bgPrimary : '#ffffff',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '16px',
      padding: '20px',
      paddingBottom: '24px',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    cardHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '16px',
      flexShrink: 0,
    },
    cardNumber: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      fontWeight: 600,
      color: colors.accentPrimary,
    },
    removeBtn: {
      width: '32px',
      height: '32px',
      borderRadius: '8px',
      border: 'none',
      backgroundColor: 'transparent',
      color: colors.textTertiary,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s ease',
    },
    inputGroup: {
      marginBottom: '16px',
      flexShrink: 0,
    },
    inputGroupLast: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    inputLabel: {
      fontSize: '11px',
      fontWeight: 600,
      color: colors.textSecondary,
      marginBottom: '8px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    input: {
      width: '100%',
      padding: '12px 14px',
      backgroundColor: isDark ? colors.bgSecondary : '#f9fafb',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '10px',
      fontSize: '14px',
      color: colors.textPrimary,
      outline: 'none',
      fontFamily: 'inherit',
      transition: 'border-color 0.2s ease',
    },
    textarea: {
      width: '100%',
      flex: 1,
      minHeight: '80px',
      padding: '12px 14px',
      backgroundColor: isDark ? colors.bgSecondary : '#f9fafb',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '10px',
      fontSize: '14px',
      color: colors.textPrimary,
      resize: 'none',
      outline: 'none',
      fontFamily: 'inherit',
      transition: 'border-color 0.2s ease',
    },
    addCard: {
      width: `${CARD_WIDTH}px`,
      minWidth: `${CARD_WIDTH}px`,
      minHeight: `${CARD_MIN_HEIGHT}px`,
      backgroundColor: 'transparent',
      border: `2px dashed ${colors.borderColor}`,
      borderRadius: '16px',
      padding: '20px',
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
      cursor: items.length >= maxItems ? 'not-allowed' : 'pointer',
      opacity: items.length >= maxItems ? 0.5 : 1,
      transition: 'all 0.2s ease',
    },
    addIcon: {
      width: '56px',
      height: '56px',
      borderRadius: '14px',
      backgroundColor: `${colors.accentPrimary}15`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: colors.accentPrimary,
    },
    addText: {
      fontSize: '15px',
      fontWeight: 600,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    addSubtext: {
      fontSize: '13px',
      color: colors.textTertiary,
    },
    footer: {
      display: 'flex',
      gap: '12px',
      marginTop: '8px',
    },
    skipBtn: {
      padding: '14px 24px',
      backgroundColor: 'transparent',
      border: `1px solid ${colors.borderColor}`,
      borderRadius: '10px',
      color: colors.textSecondary,
      fontSize: '14px',
      cursor: 'pointer',
    },
    submitBtn: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '14px',
      backgroundColor: validCount > 0 ? colors.accentPrimary : colors.borderColor,
      color: validCount > 0 ? 'white' : colors.textTertiary,
      border: 'none',
      borderRadius: '10px',
      fontSize: '14px',
      fontWeight: 600,
      cursor: validCount > 0 ? 'pointer' : 'not-allowed',
    },
  };

  return (
    <div style={styles.container}>
      {/* Scroll horizontal de cards - vai até a borda da tela */}
      <div 
        ref={scrollRef}
        style={styles.scrollContainer}
        className="faq-scroll"
      >
        <div style={styles.cardsWrapper}>
          {/* Cards de FAQ */}
          {items.map((item, index) => (
            <div key={item.id} style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.cardNumber}>
                  <HelpCircle size={18} />
                  FAQ #{index + 1}
                </div>
                {items.length > 1 && (
                  <button
                    style={styles.removeBtn}
                    onClick={() => removeItem(item.id)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#fee2e2';
                      e.currentTarget.style.color = '#ef4444';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = colors.textTertiary;
                    }}
                  >
                    <Trash2 size={18} />
                  </button>
                )}
              </div>
              
              <div style={styles.inputGroup}>
                <div style={styles.inputLabel}>Pergunta</div>
                <input
                  style={styles.input}
                  placeholder="Ex: Qual o horário de funcionamento?"
                  value={item.question}
                  onChange={(e) => updateItem(item.id, 'question', e.target.value)}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.accentPrimary;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = colors.borderColor;
                  }}
                />
              </div>
              
              <div style={styles.inputGroupLast}>
                <div style={styles.inputLabel}>Resposta</div>
                <textarea
                  style={styles.textarea}
                  placeholder="Ex: Funcionamos de segunda a sexta, das 9h às 18h."
                  value={item.answer}
                  onChange={(e) => updateItem(item.id, 'answer', e.target.value)}
                  onFocus={(e) => {
                    e.target.style.borderColor = colors.accentPrimary;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = colors.borderColor;
                  }}
                />
              </div>
            </div>
          ))}

          {/* Card de adicionar novo */}
          <div
            style={styles.addCard}
            onClick={() => items.length < maxItems && addItem()}
            onMouseEnter={(e) => {
              if (items.length < maxItems) {
                e.currentTarget.style.borderColor = colors.accentPrimary;
                e.currentTarget.style.backgroundColor = `${colors.accentPrimary}05`;
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = colors.borderColor;
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <div style={styles.addIcon}>
              <Plus size={28} />
            </div>
            <div style={styles.addText}>Novo FAQ</div>
            <div style={styles.addSubtext}>
              {items.length}/{maxItems} perguntas
            </div>
          </div>
        </div>
      </div>

      {/* Footer com ações */}
      <div style={styles.footer}>
        <button style={styles.skipBtn} onClick={onSkip}>
          {skipText || 'Pular'}
        </button>
        <button
          style={styles.submitBtn}
          onClick={handleSubmit}
          disabled={validCount === 0}
        >
          {validCount > 0 ? `Salvar ${validCount} FAQ${validCount > 1 ? 's' : ''}` : 'Preencha um FAQ'}
          {validCount > 0 && <ArrowRight size={18} />}
        </button>
      </div>

      {/* CSS para scrollbar */}
      <style>{`
        .faq-scroll::-webkit-scrollbar {
          height: 6px;
        }
        .faq-scroll::-webkit-scrollbar-track {
          background: ${colors.borderColor};
          border-radius: 3px;
        }
        .faq-scroll::-webkit-scrollbar-thumb {
          background: ${colors.textTertiary};
          border-radius: 3px;
        }
        .faq-scroll::-webkit-scrollbar-thumb:hover {
          background: ${colors.textSecondary};
        }
      `}</style>
    </div>
  );
};

export default {
  TextOnlyInput,
  TextAudioInput,
  OptionsInput,
  OptionsCustomInput,
  MultiselectTextInput,
  FAQCardsInput
};